import { cocartClient } from './client';
import { getNonce } from './nonceStorage';

// ============================================================
// TYPY
// ============================================================
export interface CartItem {
    item_key: string;
    product_id: number;
    variation_id?: number;
    quantity: number;
    data: {
        name: string;
        price: string;
        regular_price?: string;
        sale_price?: string;
        stock_status?: string;
        description?: string;
        sku?: string;
    };
    line_subtotal: string;
    line_total: string;
    line_subtotal_tax: string;
    line_tax: string;
    image?: {
        src: string;
        alt: string;
    };
    variation?: Record<string, string>;
}

export interface CartTotals {
    total: string;
    subtotal: string;
    shipping_total: string;
    fee_total: string;
    discount_total: string;
    total_tax: string;
    currency_code: string;
    currency_symbol: string;
}

export interface CartResponse {
    cart_hash: string;
    cart_key: string;
    items: CartItem[];
    item_count: number;
    items_weight: string;
    totals: {
        subtotal: string;
        subtotal_tax: string;
        fee_total: string;
        fee_tax: string;
        discount_total: string;
        discount_tax: string;
        shipping_total: string;
        shipping_tax: string;
        total: string;
        total_tax: string;
    };
    coupons: any[];
    needs_payment: boolean;
    needs_shipping: boolean;
    currency: {
        currency_code: string;
        currency_symbol: string;
    };
}

export interface AddToCartResponse extends CartResponse {
    message: string;
    product_name: string;
}

// ============================================================
// ZARZĄDZANIE CART_KEY W LOCALSTORAGE
// ============================================================
const CART_KEY_STORAGE = 'cocart_cart_key';

export const getCartKey = (): string | null => {
    return localStorage.getItem(CART_KEY_STORAGE);
};

export const setCartKey = (key: string): void => {
    if (key && !getNonce()) {
        localStorage.setItem(CART_KEY_STORAGE, key);
    }
};

export const clearCartKey = (): void => {
    localStorage.removeItem(CART_KEY_STORAGE);
};

// ============================================================
// POMOCNICZA FUNKCJA - dodaje cart_key do URL jeśli istnieje
// ============================================================
// WAŻNE: jeśli jesteśmy zalogowani (mamy nonce z mu-pluginu SSO), NIE
// doklejamy zapisanego wcześniej cart_key gościa. CoCart, widząc
// zalogowanego użytkownika (ciasteczko + nonce), sam identyfikuje
// koszyk po ID konta — wymuszanie starego cart_key gościa uniemożliwiłoby
// przełączenie się na koszyk zalogowanego użytkownika.
const addCartKeyToUrl = (url: string): string => {
    if (getNonce()) return url;

    const cartKey = getCartKey();
    if (!cartKey) return url;

    const separator = url.includes('?') ? '&' : '?';
    return `${url}${separator}cart_key=${encodeURIComponent(cartKey)}`;
};

// ============================================================
// FUNKCJE API
// ============================================================

/**
 * Pobiera zawartość koszyka
 * GET /wp-json/cocart/v2/cart?cart_key=...
 */
export const getCart = async (signal?: AbortSignal): Promise<CartResponse> => {
    try {
        const url = addCartKeyToUrl('/cart');

        const response = await cocartClient.get(url, { signal });

        if (response.data?.cart_key) {
            setCartKey(response.data.cart_key);
        }

        return response.data;
    } catch (error: any) {
        if (error.response?.status === 404) {
            return {
                cart_hash: '',
                cart_key: '',
                items: [],
                item_count: 0,
                items_weight: '0',
                totals: {
                    subtotal: '0',
                    subtotal_tax: '0',
                    fee_total: '0',
                    fee_tax: '0',
                    discount_total: '0',
                    discount_tax: '0',
                    shipping_total: '0',
                    shipping_tax: '0',
                    total: '0',
                    total_tax: '0',
                },
                coupons: [],
                needs_payment: false,
                needs_shipping: false,
                currency: {
                    currency_code: 'PLN',
                    currency_symbol: 'zł',
                },
            };
        }
        if (error.code !== 'ERR_CANCELED') {
            console.error('❌ Błąd pobierania koszyka:', error);
        }
        throw error;
    }
};

/**
 * Dodaje produkt do koszyka
 * POST /wp-json/cocart/v2/cart/add-item?cart_key=...
 */
export const addToCart = async (params: {
    id: number | string;
    quantity?: number | string;
    variation_id?: number | string;
    variation?: Record<string, string>;
}): Promise<AddToCartResponse> => {
    try {
        const url = addCartKeyToUrl('/cart/add-item');

        const formData = new URLSearchParams();
        formData.append('id', String(params.id));
        formData.append('quantity', String(params.quantity || 1));

        if (params.variation_id) {
            formData.append('variation_id', String(params.variation_id));
        }

        if (params.variation && Object.keys(params.variation).length > 0) {
            Object.entries(params.variation).forEach(([key, value]) => {
                formData.append(`variation[${key}]`, String(value));
            });
        }

        const response = await cocartClient.post(url, formData.toString(), {
            headers: {
                'Content-Type': 'application/x-www-form-urlencoded',
            },
        });

        if (response.data?.cart_key) {
            setCartKey(response.data.cart_key);
        }

        return response.data;
    } catch (error) {
        console.error('❌ Błąd dodawania do koszyka:', error);
        throw error;
    }
};

/**
 * Aktualizuje ilość produktu w koszyku
 * POST /wp-json/cocart/v2/cart/item/{item_key}?cart_key=...
 */
export const updateCartItem = async (params: {
    item_key: string;
    quantity: number;
}): Promise<CartResponse> => {
    try {
        // Używamy POST na /cart/item/{item_key} (tak samo jak DELETE)
        const url = addCartKeyToUrl(`/cart/item/${encodeURIComponent(params.item_key)}`);

        const formData = new URLSearchParams();
        formData.append('quantity', String(params.quantity));

        const response = await cocartClient.post(url, formData.toString(), {
            headers: {
                'Content-Type': 'application/x-www-form-urlencoded',
            },
        });

        if (response.data?.cart_key) {
            setCartKey(response.data.cart_key);
        }

        return response.data;
    } catch (error) {
        console.error('❌ Błąd aktualizacji koszyka:', error);
        throw error;
    }
};

/**
 * Usuwa produkt z koszyka
 * DELETE /wp-json/cocart/v2/cart/item/{item_key}?cart_key=...
 */
export const removeFromCart = async (item_key: string): Promise<{ success: boolean }> => {
    try {
        const url = addCartKeyToUrl(`/cart/item/${encodeURIComponent(item_key)}`);

        const response = await cocartClient.delete(url);

        if (response.data?.cart_key) {
            setCartKey(response.data.cart_key);
        }

        return { success: true };
    } catch (error) {
        console.error('❌ Błąd usuwania z koszyka:', error);
        throw error;
    }
};

/**
 * Czyści cały koszyk
 * POST /wp-json/cocart/v2/cart/clear?cart_key=...
 */
export const clearCart = async (): Promise<{ success: boolean }> => {
    try {
        const url = addCartKeyToUrl('/cart/clear');

        const response = await cocartClient.post(url, {});
        clearCartKey();
        return response.data;
    } catch (error) {
        console.error('❌ Błąd czyszczenia koszyka:', error);
        throw error;
    }
};
